var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { styled, Avatar, Button as MuiButton, Box, Typography, } from "@mui/material";
import convertPxToRem from "../../../../util-functions/convertPxToRem";
import { Icon } from "../../../Icon";
import { useFileUploadContext } from "../useFileUploadContext";
import { formatDescription } from "../fileUploadUtil";
import { Button } from "../../../Button";
export const StyledUploadButton = styled(MuiButton, {
    shouldForwardProp: (prop) => prop !== "fullWidth",
})(({ theme, fullWidth }) => ({
    height: convertPxToRem(64),
    width: fullWidth ? "100%" : convertPxToRem(414),
    padding: convertPxToRem(16),
    display: "flex",
    justifyContent: "start",
    borderRadius: convertPxToRem(16),
    transition: `all 0.2s`,
    backgroundColor: theme.palette.grey01.main,
    border: `${convertPxToRem(1)} solid ${theme.palette.grey05.main}`,
    "&:hover": {
        backgroundColor: theme.palette.grey03.main,
    },
    "&:active": {
        transform: `scale(0.98)`,
    },
    "&:disabled": {
        color: theme.palette.disabledText.main,
        backgroundColor: theme.palette.grey03.main,
    },
    "&.Mui-focusVisible": {
        border: `${convertPxToRem(2.5)} solid ${theme.palette.primaryBlack.main}`,
    },
}));
export const StyledUploadButtonAvatar = styled(Avatar)(({ theme }) => ({
    width: convertPxToRem(28),
    height: convertPxToRem(28),
    backgroundColor: theme.palette.sativaGreen.main,
    marginRight: convertPxToRem(8),
}));
export const StyledUploadButtonDescription = styled(Typography)(({ theme }) => ({
    color: theme.palette.tertiaryText.main,
    textAlign: "start",
    maxWidth: convertPxToRem(305),
    marginTop: convertPxToRem(8),
    marginBottom: convertPxToRem(8),
}));
export const StyledUploadButtonLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.primaryBlackText.main,
    width: convertPxToRem(90),
    margin: `${convertPxToRem(6)} ${convertPxToRem(16)} ${convertPxToRem(6)} 0`,
}));
export const StyledUploadManagerButtonContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "fullWidth",
})(({ fullWidth }) => ({
    display: "flex",
    flexDirection: "column",
    width: fullWidth ? "100%" : convertPxToRem(414),
}));
export const StyledPreview = styled(Box)(({ theme }) => ({
    marginTop: convertPxToRem(8),
    padding: convertPxToRem(16),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: convertPxToRem(16),
    backgroundColor: theme.palette.grey01.main,
    border: `${convertPxToRem(1)} solid ${theme.palette.grey05.main}`,
}));
export const StyledPreviewNameContainer = styled("span")(() => ({
    display: "flex",
    padding: 0,
    gap: convertPxToRem(8),
    alignItems: "center",
    justifyContent: "center",
}));
export const StyledPreviewRemoveContainer = styled("span")(() => ({
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
}));
export const UploadButton = ({ contentTypes, fullWidth, label, maxFileSizeInMegaBytes, onChange, showContentTypes, showMaxFileSize, testId, }) => {
    const description = formatDescription({
        contentTypes,
        maxFileSizeInMegaBytes,
        showMaxFileSize,
        showContentTypes,
    });
    const fileInputRef = useRef(null);
    const handleClick = () => {
        if (fileInputRef.current !== null) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };
    return (_jsxs(StyledUploadButton, Object.assign({ "data-testid": testId, onClick: handleClick, fullWidth: fullWidth }, { children: [_jsx("input", { "data-testid": testId && `input-${testId}`, hidden: true, accept: contentTypes.join(","), type: "file", onChange: onChange, ref: fileInputRef }), _jsx(StyledUploadButtonAvatar, Object.assign({ "data-testid": testId && `avatar-${testId}` }, { children: _jsx(Icon, { testId: testId && `icon-${testId}`, iconName: "Import", fontSize: "small", color: "secondary" }) })), _jsx(StyledUploadButtonLabel, Object.assign({ "data-testid": testId && `label-${testId}`, variant: "mediumTextStrong" }, { children: label })), description && (_jsx(StyledUploadButtonDescription, Object.assign({ "data-testid": testId && `description-${testId}`, variant: "smallText" }, { children: description })))] })));
};
export const FileUploadManagerButton = ({ fullWidth = false, label = "Choose file", showContentTypes = false, showMaxFileSize = false, testId, }) => {
    const { contentTypes, filesWaiting, maxFileSizeInMegaBytes, handleOnChange, handleRemoveFile, uploadImmediately, } = useFileUploadContext();
    return (_jsxs(StyledUploadManagerButtonContainer, Object.assign({ "data-testid": testId && `container-${testId}`, fullWidth: fullWidth }, { children: [_jsx(UploadButton, { contentTypes: contentTypes, fullWidth: true, label: label, maxFileSizeInMegaBytes: maxFileSizeInMegaBytes, onChange: handleOnChange, showContentTypes: showContentTypes, showMaxFileSize: showMaxFileSize, testId: testId }), uploadImmediately === false && (_jsx(Box, Object.assign({ "data-testid": testId && `preview-list-${testId}` }, { children: Object.keys(filesWaiting).map((fileId) => (_jsxs(StyledPreview, Object.assign({ "data-testid": testId && `preview-list-item-${testId}` }, { children: [_jsxs(StyledPreviewNameContainer, Object.assign({ "data-testid": testId && `preview-list-item-name-${testId}` }, { children: [_jsx(Icon, { iconName: "Checkmark", color: "green10" }), _jsx(Typography, Object.assign({ variant: "mediumText" }, { children: filesWaiting[fileId].name }))] })), _jsx(StyledPreviewRemoveContainer, Object.assign({ onClick: () => handleRemoveFile(fileId), "data-testid": testId && `preview-list-item-remove-${testId}` }, { children: _jsx(Icon, { iconName: "Close" }) }))] }), fileId))) })))] })));
};
FileUploadManagerButton.displayName = "FileUploadManager.Button";
export const FileUploadManagerSubmit = ({ label = "Upload", testId, }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { filesWaiting, handleUploadFile, handleRemoveFile, uploadImmediately, } = useFileUploadContext();
    const handleStartUploads = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        yield Promise.all(Object.keys(filesWaiting).map((fileId) => __awaiter(void 0, void 0, void 0, function* () {
            const uploadFileResult = yield handleUploadFile(filesWaiting[fileId]);
            if (uploadFileResult.isSuccess === true) {
                handleRemoveFile(fileId);
            }
        })));
        setIsSubmitting(false);
    });
    const hasFiles = Object.keys(filesWaiting).length > 0;
    return uploadImmediately ? null : (_jsx(Button, { testId: testId, label: label, onClick: handleStartUploads, disabled: !hasFiles || isSubmitting }));
};
FileUploadManagerSubmit.displayName = "FileUploadManager.Submit";
